import * as React from 'react'
import styled, { css } from 'styled-components'

import { Tooltip } from '../../../Tooltip'

import * as colors from '../../../../constants/colors'
import * as glyphs from '../../../../icons/glyphs'
import * as styles from '../../../../styles'

import { RoundButton, RoundButtonProps } from '../../../button/RoundButton'
import { WysiwygContent } from '../../../WysiwygContent'

import * as type from '../type'
import * as S from '../../../../styles'

function RewardCardComponent({
  className,
  deliveryDateTag,
  disabled,
  fullWidth = false,
  hideCta,
  isMultiVariationsEnabled = false,
  isProjectFinished,
  onSelect,
  reward,
  selected = false,
  stockTag,
  tag,
  translations,
  mainImage,
}: type.RewardCardProps): React.ReactElement {
  const options = isMultiVariationsEnabled ? reward.options : reward.variants
  const hasOptions = Boolean(options && options.length > 0)

  const hasAvailableStock = reward.stock.available == null || reward.stock.available > 0

  const stockAvailable = reward.stock.available ?? undefined
  const hasStock = stockAvailable !== undefined && stockAvailable > 0

  const hasOptionsAndStock = Boolean(
    hasOptions &&
      options?.some((option) => {
        if ('values' in option) {
          return option.values.some(
            (value) =>
              value.stock.available === null || value.stock.available === undefined || value.stock.available > 0,
          )
        } else {
          return option.stock.available === null || option.stock.available === undefined || option.stock.available > 0
        }
      }),
  )

  return (
    <styles.card.reward.CardWrapper
      className={className}
      fullWidth={fullWidth}
      bordered={reward.isFeatured || reward.isHidden}
      borderColor={
        reward.isFeatured ? colors.PRIMARY_BLUE_100 : reward.isHidden ? colors.SECONDARY_GREEN_200 : undefined
      }
    >
      {tag}
      {mainImage ?? (reward.img && <styles.card.reward.CardImage src={reward.img} alt={reward.title} />)}

      <styles.card.reward.CardTitle>
        <styles.card.reward.Price>{translations.price}</styles.card.reward.Price>
        {reward.title}
        {translations.options !== undefined && (
          <styles.card.reward.NumberOfOptions>{translations.options}</styles.card.reward.NumberOfOptions>
        )}
      </styles.card.reward.CardTitle>

      {!hideCta && (
        <>
          {isProjectFinished ? (
            <BaseCTA disabled type="button">
              {translations.finished}
            </BaseCTA>
          ) : !hasAvailableStock || (hasOptions && !hasOptionsAndStock) ? (
            <BaseCTA disabled type="button">
              {translations.soldOut}
            </BaseCTA>
          ) : selected && !hasOptions ? (
            <BaseCTA
              bordered={selected}
              onClick={() => onSelect.reward()}
              inverted={selected}
              type="button"
              prefixIcon={<glyphs.stroke.Minus size={18} />}
            >
              {translations.remove}
            </BaseCTA>
          ) : (
            <CTAWrapper>
              <BaseCTA
                bordered={selected}
                disabled={disabled && !hasOptions}
                onClick={() => onSelect.reward()}
                inverted={selected}
                type="button"
                prefixIcon={<glyphs.stroke.Plus size={18} />}
              >
                {translations.cta}
              </BaseCTA>
              {disabled && !hasOptions && (
                <Tooltip open position="bottom">
                  <S.copy.S>{translations.disabledCTA}</S.copy.S>
                </Tooltip>
              )}
            </CTAWrapper>
          )}
        </>
      )}

      {translations.quantitySelected && (
        <styles.card.reward.QuantitySelected>
          <glyphs.fill.CheckCircle size={14} />
          {translations.quantitySelected}
        </styles.card.reward.QuantitySelected>
      )}

      {reward.taxDeductible && translations.taxDeduction && (
        <styles.card.reward.TaxDeduction>
          <glyphs.stroke.Percent size={16} />
          {translations.taxDeduction}
        </styles.card.reward.TaxDeduction>
      )}

      {reward.description.__html && (
        <WysiwygContent as={styles.card.reward.CardContent}>
          <styles.card.reward.CardDescription as="div" dangerouslySetInnerHTML={reward.description} />
        </WysiwygContent>
      )}

      <styles.card.reward.CardFooter>
        {(deliveryDateTag || stockTag) && (
          <styles.card.reward.Tags>
            {deliveryDateTag}
            {stockTag}
          </styles.card.reward.Tags>
        )}

        {hasStock && translations.stockAvailable && (
          <styles.card.reward.CardSubsRemaining>
            <styles.heading.XXXXXS>{translations.stockAvailable}</styles.heading.XXXXXS>
            {stockAvailable <= 5 && <styles.heading.XXXXXS>{translations.soonSoldOut}</styles.heading.XXXXXS>}
          </styles.card.reward.CardSubsRemaining>
        )}
      </styles.card.reward.CardFooter>

      {translations.convertedCurrency && (
        <styles.card.reward.CurrencyConversion>{translations.convertedCurrency}</styles.card.reward.CurrencyConversion>
      )}
    </styles.card.reward.CardWrapper>
  )
}

export const Card = styled(RewardCardComponent)``

type BaseCTAProps = RoundButtonProps & { bordered?: boolean }

const BaseCTA = styled(RoundButton)<BaseCTAProps>`
  align-self: center;
  width: fit-content;

  ${({ bordered }) =>
    bordered &&
    css`
      border: 2px solid ${colors.PRIMARY_BLUE_500};
    `};
`

const CTAWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;

  ${S.tooltip.Tooltip} {
    display: none;
  }

  &:hover {
    ${S.tooltip.Tooltip} {
      display: block;
    }
  }
`
